import React, { useState, useEffect } from 'react';
import './CustomerFormBuilder.css';

const CustomerFormBuilder = ({ product, onSave }) => {
    const [formConfig, setFormConfig] = useState({
        requiredFields: ['name', 'email'], // Always required
        optionalFields: [],
        requiredOptionalFields: [], // Optional fields marked as required
        customFields: [],
        ...product.customerFormConfig
    });
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved'
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize form config from product data
    useEffect(() => {
        if (product.customerFormConfig && !isInitialized) {
            setFormConfig({
                requiredFields: ['name', 'email'],
                optionalFields: [],
                requiredOptionalFields: [],
                customFields: [],
                ...product.customerFormConfig
            });
            setIsInitialized(true);
            console.log('🔄 CustomerFormBuilder: Initialized with product config');
        }
    }, [product.customerFormConfig, isInitialized]);

    // Available field types
    const availableFields = [
        { id: 'name', label: 'Full Name', type: 'text', icon: 'fas fa-user', required: true },
        { id: 'email', label: 'Email Address', type: 'email', icon: 'fas fa-envelope', required: true },
        { id: 'phone', label: 'Phone Number', type: 'tel', icon: 'fas fa-phone', required: false },
        { id: 'company', label: 'Company', type: 'text', icon: 'fas fa-building', required: false },
        { id: 'address', label: 'Address', type: 'textarea', icon: 'fas fa-map-marker-alt', required: false },
        { id: 'birthdate', label: 'Birth Date', type: 'date', icon: 'fas fa-birthday-cake', required: false },
        { id: 'website', label: 'Website', type: 'url', icon: 'fas fa-globe', required: false },
        { id: 'linkedin', label: 'LinkedIn Profile', type: 'url', icon: 'fab fa-linkedin', required: false },
        { id: 'instagram', label: 'Instagram Handle', type: 'text', icon: 'fab fa-instagram', required: false },
        { id: 'referral', label: 'How did you hear about us?', type: 'select', icon: 'fas fa-bullhorn', required: false },
        { id: 'experience', label: 'Experience Level', type: 'select', icon: 'fas fa-chart-bar', required: false },
        { id: 'goals', label: 'Your Goals', type: 'textarea', icon: 'fas fa-target', required: false }
    ];

    const handleFieldToggle = (fieldId) => {
        const field = availableFields.find(f => f.id === fieldId);
        if (field.required) return; // Can't toggle required fields

        const isCurrentlyOptional = formConfig.optionalFields.includes(fieldId);
        
        if (isCurrentlyOptional) {
            // Remove from optional
            setFormConfig(prev => ({
                ...prev,
                optionalFields: prev.optionalFields.filter(id => id !== fieldId)
            }));
        } else {
            // Add to optional
            setFormConfig(prev => ({
                ...prev,
                optionalFields: [...prev.optionalFields, fieldId]
            }));
        }
    };

    const addCustomField = () => {
        const newField = {
            id: `custom_${Date.now()}`,
            label: '',
            type: 'text',
            required: false,
            placeholder: ''
        };
        
        setFormConfig(prev => ({
            ...prev,
            customFields: [...prev.customFields, newField]
        }));
    };

    const updateCustomField = (index, field, value) => {
        setFormConfig(prev => ({
            ...prev,
            customFields: prev.customFields.map((customField, i) => 
                i === index ? { ...customField, [field]: value } : customField
            )
        }));
    };

    const removeCustomField = (index) => {
        setFormConfig(prev => ({
            ...prev,
            customFields: prev.customFields.filter((_, i) => i !== index)
        }));
    };

    // Auto-save when config changes (with proper initialization check)
    useEffect(() => {
        // Don't save if not initialized yet
        if (!isInitialized) {
            return;
        }

        // Don't save if already saving
        if (saveStatus === 'saving') {
            return;
        }

        // Only save if config actually changed from initial state
        const initialConfig = {
            requiredFields: ['name', 'email'],
            optionalFields: [],
            requiredOptionalFields: [],
            customFields: [],
            ...product.customerFormConfig
        };
        
        const hasChanged = JSON.stringify(formConfig) !== JSON.stringify(initialConfig);

        if (hasChanged) {
            console.log('🔄 CustomerFormBuilder: Config changed, starting save...');
            setSaveStatus('saving');
            
            const timeoutId = setTimeout(async () => {
                try {
                    await onSave({ customerFormConfig: formConfig });
                    console.log('✅ CustomerFormBuilder: Config saved successfully');
                    setSaveStatus('saved');
                    setTimeout(() => setSaveStatus('idle'), 1500);
                } catch (error) {
                    console.error('❌ CustomerFormBuilder: Error saving form config:', error);
                    setSaveStatus('idle');
                }
            }, 800);

            return () => clearTimeout(timeoutId);
        } else {
            // Reset to idle if no changes
            if (saveStatus !== 'idle') {
                setSaveStatus('idle');
            }
        }
    }, [formConfig, onSave, product.customerFormConfig, isInitialized, saveStatus]);

    const getFieldStatus = (fieldId) => {
        const field = availableFields.find(f => f.id === fieldId);
        if (field.required) return 'required'; // Always required (name, email)
        if (formConfig.requiredOptionalFields.includes(fieldId)) return 'required-optional'; // Optional field marked as required
        if (formConfig.optionalFields.includes(fieldId)) return 'optional';
        return 'disabled';
    };

    const isFieldActive = (fieldId) => {
        return formConfig.optionalFields.includes(fieldId) || formConfig.requiredOptionalFields.includes(fieldId);
    };

    const isFieldRequired = (fieldId) => {
        const field = availableFields.find(f => f.id === fieldId);
        return field.required || formConfig.requiredOptionalFields.includes(fieldId);
    };

    const getAllActiveFields = () => {
        return [
            ...formConfig.requiredFields.map(id => ({ ...availableFields.find(f => f.id === id), status: 'required' })),
            ...formConfig.requiredOptionalFields.map(id => ({ ...availableFields.find(f => f.id === id), status: 'required-optional' })),
            ...formConfig.optionalFields.map(id => ({ ...availableFields.find(f => f.id === id), status: 'optional' })),
            ...formConfig.customFields.map(field => ({ ...field, status: field.required ? 'custom-required' : 'custom' }))
        ];
    };

    return (
        <div className="sales-form-builder">
            <div className="sales-form-builder-header">
                <div className="sales-header-content">
                    <h4>Customer Data Collection</h4>
                    <p className="sales-builder-description">
                        Configure what information you want to collect when customers purchase this {product.type === 'event' ? 'event' : 'product'}.
                    </p>
                </div>
                <div className="sales-save-status">
                    {saveStatus === 'saving' && (
                        <span className="sales-status-indicator saving">
                            <div className="sales-mini-spinner"></div>
                            Saving...
                        </span>
                    )}
                    {saveStatus === 'saved' && (
                        <span className="sales-status-indicator saved">
                            ✅ Saved
                        </span>
                    )}
                </div>
            </div>

            {/* Form Preview */}
            <div className="sales-form-preview">
                <h5>
                    <i className="fas fa-eye"></i>
                    Form Preview
                </h5>
                <div className="sales-preview-form">
                    <div className="sales-preview-header">
                        <h6>Purchase {product.title}</h6>
                        <p>Please fill out the information below to complete your purchase.</p>
                    </div>
                    
                    <div className="sales-preview-fields">
                        {getAllActiveFields().map((field, index) => (
                            <div key={field.id || index} className={`sales-preview-field ${field.status}`}>
                                <label>
                                    <i className={`${field.icon} sales-field-icon`}></i>
                                    {field.label}
                                    {field.status === 'required' && <span className="sales-required">*</span>}
                                </label>
                                <div className="sales-preview-input">
                                    {field.type === 'textarea' ? (
                                        <div className="sales-input-placeholder textarea">Textarea input...</div>
                                    ) : field.type === 'select' ? (
                                        <div className="sales-input-placeholder select">Select dropdown...</div>
                                    ) : (
                                        <div className="sales-input-placeholder">Text input...</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Field Configuration */}
            <div className="sales-field-configuration">
                <h5>
                    <i className="fas fa-cog"></i>
                    Available Fields
                </h5>
                <div className="sales-fields-grid">
                    {availableFields.map(field => {
                        const status = getFieldStatus(field.id);
                        const isActive = isFieldActive(field.id);
                        const isRequired = isFieldRequired(field.id);
                        
                        return (
                            <div 
                                key={field.id} 
                                className={`sales-field-option ${status}`}
                            >
                                <div 
                                    className="sales-field-main"
                                    onClick={() => !field.required && handleFieldToggle(field.id)}
                                >
                                    <div className="sales-field-info">
                                        <i className={`${field.icon} sales-field-icon`}></i>
                                        <div>
                                            <h6>{field.label}</h6>
                                            <p>{field.type}</p>
                                        </div>
                                    </div>
                                    <div className="sales-field-status">
                                        {status === 'required' && <span className="sales-status-badge required">Always Required</span>}
                                        {status === 'required-optional' && <span className="sales-status-badge required-optional">Required</span>}
                                        {status === 'optional' && <span className="sales-status-badge optional">Optional</span>}
                                        {status === 'disabled' && <span className="sales-status-badge disabled">Disabled</span>}
                                    </div>
                                </div>
                                
                                {/* Required Toggle for active optional fields */}
                                {isActive && !field.required && (
                                    <div className="sales-field-controls">
                                        <label className="sales-required-toggle">
                                            <input
                                                type="checkbox"
                                                checked={formConfig.requiredOptionalFields.includes(field.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        // Move from optional to required-optional
                                                        setFormConfig(prev => ({
                                                            ...prev,
                                                            optionalFields: prev.optionalFields.filter(id => id !== field.id),
                                                            requiredOptionalFields: [...prev.requiredOptionalFields, field.id]
                                                        }));
                                                    } else {
                                                        // Move from required-optional to optional
                                                        setFormConfig(prev => ({
                                                            ...prev,
                                                            requiredOptionalFields: prev.requiredOptionalFields.filter(id => id !== field.id),
                                                            optionalFields: [...prev.optionalFields, field.id]
                                                        }));
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <span>Make Required</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Custom Fields */}
            <div className="sales-custom-fields">
                <h5>Custom Fields</h5>
                <p className="sales-section-description">
                    Add your own custom fields for specific information you need.
                </p>
                
                {formConfig.customFields.map((field, index) => (
                    <div key={index} className="sales-custom-field-editor">
                        <div className="sales-custom-field-header">
                            <h6>Custom Field {index + 1}</h6>
                            <button 
                                className="sales-remove-custom-field"
                                onClick={() => removeCustomField(index)}
                                title="Remove custom field"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="sales-custom-field-config">
                            <div className="sales-form-row">
                                <div className="sales-form-group">
                                    <label>Field Label *</label>
                                    <input
                                        type="text"
                                        value={field.label}
                                        onChange={(e) => updateCustomField(index, 'label', e.target.value)}
                                        placeholder="e.g. Industry, Job Title, etc."
                                    />
                                </div>
                                <div className="sales-form-group">
                                    <label>Field Type</label>
                                    <select
                                        value={field.type}
                                        onChange={(e) => updateCustomField(index, 'type', e.target.value)}
                                    >
                                        <option value="text">Text Input</option>
                                        <option value="email">Email</option>
                                        <option value="tel">Phone</option>
                                        <option value="url">URL</option>
                                        <option value="date">Date</option>
                                        <option value="textarea">Long Text</option>
                                        <option value="select">Dropdown</option>
                                        <option value="checkbox">Checkbox</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div className="sales-form-group">
                                <label>Placeholder Text</label>
                                <input
                                    type="text"
                                    value={field.placeholder}
                                    onChange={(e) => updateCustomField(index, 'placeholder', e.target.value)}
                                    placeholder="Placeholder text for this field"
                                />
                            </div>
                            
                            <div className="sales-form-group">
                                <label className="sales-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={field.required}
                                        onChange={(e) => updateCustomField(index, 'required', e.target.checked)}
                                    />
                                    <span>Required field</span>
                                </label>
                            </div>
                        </div>
                    </div>
                ))}
                
                <button 
                    className="sales-add-custom-field-btn"
                    onClick={addCustomField}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Add Custom Field
                </button>
            </div>

            {/* Form Summary */}
            <div className="sales-form-summary">
                <h5>Form Summary</h5>
                <div className="sales-summary-stats">
                    <div className="sales-summary-stat">
                        <span className="sales-stat-number">
                            {formConfig.requiredFields.length + formConfig.requiredOptionalFields.length + formConfig.customFields.filter(f => f.required).length}
                        </span>
                        <span className="sales-stat-label">Required Fields</span>
                    </div>
                    <div className="sales-summary-stat">
                        <span className="sales-stat-number">
                            {formConfig.optionalFields.length + formConfig.customFields.filter(f => !f.required).length}
                        </span>
                        <span className="sales-stat-label">Optional Fields</span>
                    </div>
                    <div className="sales-summary-stat">
                        <span className="sales-stat-number">{formConfig.customFields.length}</span>
                        <span className="sales-stat-label">Custom Fields</span>
                    </div>
                </div>
                <p className="sales-summary-note">
                    This form will be shown to customers during checkout. Keep it simple to maximize conversions.
                </p>
            </div>
        </div>
    );
};

export default CustomerFormBuilder;
